/**
 * .tuMetrics {"tu1": 0.94, "tu2": 0.96, "tu3": 2, "notes": "Pure banner/ack rules for version.json", "tuVersion": "2.5"}
 * .help "Decides whether a new version.json is a major What's-new announcement or a quiet refresh."
 */

export interface VersionInfo {
  buildTime: string;
  gitCommit: string;
  packageVersion: string;
  releaseSummary?: string | null;
  /** major = blurb + What's new; silent = still prompt refresh, no blurb. Default major. */
  refreshAnnouncement?: 'major' | 'silent' | null;
  releaseNotesVersion?: string | null;
  releaseNotesReleasedAt?: string | null;
}

export type UpdateBannerMode = 'major' | 'refresh-only';

export const RELEASE_VERSION_ACK_KEY = 'lastAcknowledgedReleaseVersion';
/** @deprecated Legacy key — migrated into RELEASE_VERSION_ACK_KEY on read */
export const RELEASE_SIGNATURE_STORAGE_KEY = 'lastReleaseSignatureShown';
export const RELEASE_SUMMARY_STORAGE_KEY = 'lastReleaseSummaryShown';
export const RELEASE_NOTES_RELEASED_AT_KEY = 'lastReleaseNotesReleasedAt';

export function releaseVersionKey(version: VersionInfo): string {
  const versionLabel = version.releaseNotesVersion || version.packageVersion;
  const releasedAt = version.releaseNotesReleasedAt || '';
  return `${versionLabel}::${releasedAt}`;
}

export function isReleaseVersionAcknowledged(
  version: VersionInfo,
  storage: Pick<Storage, 'getItem' | 'setItem'> = localStorage,
): boolean {
  const key = releaseVersionKey(version);
  if (storage.getItem(RELEASE_VERSION_ACK_KEY) === key) {
    return true;
  }

  const versionLabel = version.releaseNotesVersion || version.packageVersion;
  const legacySig = storage.getItem(RELEASE_SIGNATURE_STORAGE_KEY);
  if (legacySig?.startsWith(`${versionLabel}::`)) {
    storage.setItem(RELEASE_VERSION_ACK_KEY, key);
    return true;
  }

  return false;
}

export function recordReleaseVersionAck(
  version: VersionInfo,
  storage: Pick<Storage, 'getItem' | 'setItem'> = localStorage,
): void {
  const key = releaseVersionKey(version);
  storage.setItem(RELEASE_VERSION_ACK_KEY, key);

  const summary = (version.releaseSummary || '').trim();
  if (summary) {
    storage.setItem(RELEASE_SUMMARY_STORAGE_KEY, summary);
  }
  if (version.releaseNotesReleasedAt) {
    storage.setItem(RELEASE_NOTES_RELEASED_AT_KEY, version.releaseNotesReleasedAt);
  }
}

export function decideUpdateBanner(
  version: VersionInfo,
  storage: Pick<Storage, 'getItem' | 'setItem'> = localStorage,
): { mode: UpdateBannerMode; releaseSummary: string | null } {
  const summary = (version.releaseSummary || '').trim();
  const versionAcked = isReleaseVersionAcknowledged(version, storage);
  const wantsMajor = version.refreshAnnouncement !== 'silent';
  const isMajorAnnouncement = wantsMajor && !!summary && !versionAcked;
  return {
    mode: isMajorAnnouncement ? 'major' : 'refresh-only',
    releaseSummary: isMajorAnnouncement ? summary : null,
  };
}

export function updatesHrefFor(version: VersionInfo, mode: UpdateBannerMode, updatesRoute: string): string | null {
  if (mode !== 'major') return null;
  const label = version.releaseNotesVersion || version.packageVersion;
  if (!label) return null;
  return `${updatesRoute}?version=${encodeURIComponent(label)}`;
}

export type StaleAction = 'banner' | 'activate-silent' | 'none';

/**
 * Prompt when this tab's baked identity is behind live version.json.
 * Prefer buildTime (unique per deploy); also treat a packageVersion bump as stale
 * so a ship cannot be missed if buildTimes somehow match.
 * A waiting SW with the same identity activates quietly — not a new release.
 */
export function decideStaleAction(opts: {
  runningBuildTime: string | null;
  serverBuildTime: string | null;
  waitingSW: boolean;
  runningPackageVersion?: string | null;
  serverPackageVersion?: string | null;
}): StaleAction {
  const buildNewer =
    !!opts.runningBuildTime &&
    !!opts.serverBuildTime &&
    opts.runningBuildTime !== opts.serverBuildTime;
  const versionNewer =
    !!opts.runningPackageVersion &&
    !!opts.serverPackageVersion &&
    opts.runningPackageVersion !== opts.serverPackageVersion;
  if (buildNewer || versionNewer) return 'banner';
  if (opts.waitingSW) return 'activate-silent';
  return 'none';
}
